import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { fetchData } from "../api/client";

/* ===== Normalização ===== */
const normalizeNode = (n) => ({
  id: n.id,
  id_pai: n.id_pai ?? null,
  nome: n.nome,
  cor: n.cor || null,
  tipo: n.tipo ?? null,
  token: n.token ?? null,
  temporesposta: n.temporesposta ?? null,
  tempoconclusao: n.tempoconclusao ?? null,
  status: n.status ?? 1,
  data_cadastro: n.data_cadastro ?? null,
  data_ultima_alteracao: n.data_ultima_alteracao ?? null,
  children: Array.isArray(n.children) ? n.children.map(normalizeNode) : [],
});

const indexTree = (roots) => {
  const byId = new Map();
  const parent = new Map();
  const walk = (nodes, pId = null) => {
    nodes.forEach((n) => {
      byId.set(n.id, n);
      parent.set(n.id, pId);
      if (n.children?.length) walk(n.children, n.id);
    });
  };
  walk(roots);
  return { byId, parent };
};

const hasChildren = (node) => Array.isArray(node?.children) && node.children.length > 0;

const autoOpenSingletons = (roots) => {
  const path = [];
  let level = roots;
  while (Array.isArray(level) && level.length === 1 && hasChildren(level[0])) {
    path.push(level[0]);
    level = level[0].children;
  }
  const visible = Array.isArray(level) ? level : [];
  return { path, visible };
};

const buildBreadcrumb = (path) => path.map((n) => ({ id: n.id, nome: n.nome }));

const buildLeadFilter = (path, stage) => {
  const chain = [...path.map((n) => n.id), stage?.id].filter(Boolean);
  const keys = ["id_tipo", "id_subtipo1", "id_subtipo2", "id_subtipo3"];
  const filter = {};
  chain.forEach((id, idx) => {
    if (idx < keys.length) filter[keys[idx]] = id;
  });
  return filter;
};

/* ===== Hook principal ===== */
export const useSalesPipelineStages = (opts) => {
  const { search = "", commercial = true, limit = 2000 } = opts || {};
  const scope = "comercial";

  const query = useQuery(
    ["typesTree", search, commercial, limit],
    async () => {
      const qs = new URLSearchParams();
      qs.set("tree", "1");
      qs.set("limit", String(limit));
      qs.set("scope", scope);
      if (search) qs.set("search", search);
      if (!commercial) qs.set("commercial", "0");

      const resp = await fetchData(`/types?${qs.toString()}`, "GET");
      const raw = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const roots = raw.map(normalizeNode);
      return roots;
    },
    {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    }
  );

  const roots = query.data || [];
  const { byId } = useMemo(() => indexTree(roots), [roots]);

  const [path, setPath] = useState([]);
  const [visible, setVisible] = useState([]);

  useEffect(() => {
    if (!query.isLoading && !query.error) {
      const { path: p, visible: v } = autoOpenSingletons(roots);
      setPath(p);
      setVisible(v);
    }
  }, [query.isLoading, query.error, roots]);

  const breadcrumb = useMemo(() => buildBreadcrumb(path), [path]);
  const currentLevel = path.length;

  const getNode = (id) => byId.get(id) || null;
  const hasChildrenById = (id) => hasChildren(byId.get(id));

  const drillDown = (stageId) => {
    const node = byId.get(stageId);
    if (!node || !hasChildren(node)) return false;

    const newPath = [...path, node];

    // auto-open cadeia de filhos únicos
    let level = node.children;
    const autoStack = [];
    while (Array.isArray(level) && level.length === 1 && hasChildren(level[0])) {
      autoStack.push(level[0]);
      level = level[0].children;
    }

    setPath([...newPath, ...autoStack]);
    setVisible(Array.isArray(level) ? level : []);
    return true;
  };

  const goBack = () => {
    if (!path.length) {
      const { path: p, visible: v } = autoOpenSingletons(roots);
      setPath(p);
      setVisible(v);
      return;
    }
    const newPath = path.slice(0, -1);
    const last = newPath[newPath.length - 1];
    const newVisible = last ? last.children || [] : roots;
    setPath(newPath);
    setVisible(newVisible);
  };

  const goToRoot = () => {
    const { path: p, visible: v } = autoOpenSingletons(roots);
    setPath(p);
    setVisible(v);
  };

  const getLeadFilter = (stageOrId) => {
    const node = typeof stageOrId === "object" ? stageOrId : byId.get(stageOrId);
    if (!node) return {};
    return buildLeadFilter(path, node);
  };

  const setPathByIds = (ids = []) => {
    const chain = ids.map((id) => byId.get(id)).filter(Boolean);
    if (!chain.length) {
      goToRoot();
      return;
    }
    const valid = [];
    for (let i = 0; i < chain.length; i++) {
      const node = chain[i];
      if (i === 0) {
        if (node.id_pai == null || node.id_pai === 0) valid.push(node);
        else break;
      } else {
        if (node.id_pai === chain[i - 1].id) valid.push(node);
        else break;
      }
    }
    const last = valid[valid.length - 1];
    if (!last) {
      goToRoot();
      return;
    }
    // se último tem filhos, mostra os filhos; senão mantém nível do último válido
    if (hasChildren(last)) {
      setPath(valid);
      setVisible(last.children || []);
    } else {
      setPath(valid.slice(0, -1));
      setVisible(valid.length === 1 ? roots : []);
    }
  };

  return {
    visibleStages: visible,
    path,
    breadcrumb,
    currentLevel,
    drillDown,
    goBack,
    goToRoot,
    setPathByIds,
    hasChildren: hasChildrenById,
    getLeadFilter,
    getNode,
    loading: query.isLoading,
    error: query.error || null,
    refetch: query.refetch,
  };
};

export default useSalesPipelineStages;
